import React, { useState } from "react";
import { FoCard, FoChips, FoEmpty, FoKpis, FoTable, formatEgp } from "./financeUi";
import { os } from "./osClient";
import { TextField, useOsResource } from "./osForm";
import { REPORT_TYPES } from "./osRules";

const showCell = (column, value) => {
  if (value == null || value === "") return "—";
  if (typeof value === "number" && column !== "orders" && column !== "count") return formatEgp(value);
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
};

const totalItems = (totals) => {
  if (!totals || typeof totals !== "object") return [];
  return Object.entries(totals).flatMap(([key, value]) => {
    if (value && typeof value === "object" && !Array.isArray(value)) {
      return Object.entries(value).map(([inner, innerValue]) => {
        if (innerValue && typeof innerValue === "object") {
          return { label: `${key} · ${inner}`, value: formatEgp(innerValue.amount), note: innerValue.count != null ? `${innerValue.count}` : undefined };
        }
        return { label: `${key} · ${inner}`, value: typeof innerValue === "number" ? formatEgp(innerValue) : String(innerValue ?? "—") };
      });
    }
    return [{ label: key, value: typeof value === "number" ? (key.toLowerCase().includes("margin") || key.toLowerCase().includes("rate") ? `${value}` : formatEgp(value)) : String(value ?? "—") }];
  });
};

export default function FinancialReports() {
  const [type, setType] = useState("profit-and-loss");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [vatRate, setVatRate] = useState("0.14");
  const [applied, setApplied] = useState({ type: "profit-and-loss", startDate: "", endDate: "", vatRate: "0.14" });
  const [downloading, setDownloading] = useState("");
  const report = useOsResource(
    (auth) => os.report(auth, applied.type, {
      startDate: applied.startDate,
      endDate: applied.endDate,
      vatRate: applied.type === "tax" ? applied.vatRate : undefined,
    }),
    [applied]
  );
  const columns = report.data?.columns || [];
  const rows = report.data?.rows || [];
  const items = totalItems(report.data?.totals);

  const download = async (format) => {
    setDownloading(format);
    try {
      await os.downloadReport(report.token, applied.type, {
        format,
        startDate: applied.startDate,
        endDate: applied.endDate,
        vatRate: applied.type === "tax" ? applied.vatRate : undefined,
      });
    } catch (err) {
      /* toast already shown */
    } finally {
      setDownloading("");
    }
  };

  return (
    <div className="fo-stack">
      <FoChips options={REPORT_TYPES} value={type} onChange={setType} />
      <div className="fo-tools">
        <TextField label="From" type="date" value={startDate} onChange={setStartDate} />
        <TextField label="To" type="date" value={endDate} onChange={setEndDate} />
        {type === "tax" && <TextField label="VAT rate" value={vatRate} onChange={setVatRate} hint="0 to 1. Default 0.14." />}
        <button type="button" className="fo-btn fo-btn-primary" onClick={() => setApplied({ type, startDate, endDate, vatRate })}>Generate</button>
        <button type="button" className="fo-btn" disabled={!report.token || downloading !== ""} onClick={() => download("pdf")}>{downloading === "pdf" ? "Downloading…" : "PDF"}</button>
        <button type="button" className="fo-btn" disabled={!report.token || downloading !== ""} onClick={() => download("xlsx")}>{downloading === "xlsx" ? "Downloading…" : "Spreadsheet"}</button>
      </div>
      {items.length > 0 && <FoKpis items={items} />}
      <FoCard title={report.data?.title || "Report"} note={report.data?.period || "The default period is the current month."}>
        {report.loading && <FoEmpty>Building the report…</FoEmpty>}
        {!report.loading && (
          <FoTable
            rowKey={(row, index) => `${row.line || row.brand || row.section || "row"}-${index}`}
            dataSource={rows}
            pagination={rows.length > 20 ? { pageSize: 20, total: rows.length } : false}
            empty="This report has no rows for the period."
            columns={(columns.length ? columns : ["section", "line", "amount"]).map((column) => ({
              title: column,
              dataIndex: column,
              render: (value) => showCell(column, value),
            }))}
          />
        )}
        {(report.data?.notes || []).length > 0 && (
          <ul className="fo-notes">
            {report.data.notes.map((note) => <li key={note}>{note}</li>)}
          </ul>
        )}
      </FoCard>
    </div>
  );
}
